import { useEffect, useState, useRef } from 'react';

import { DataManager, WebApiAdaptor } from '@syncfusion/ej2-data';
import { GridComponent, Inject, Page, Resize, Sort, Toolbar, ExcelExport } from '@syncfusion/ej2-react-grids';

import { useToast, useUserGroups } from '@hooks';
import { getAvaDataManager } from 'common/AVADataManager';
import { getFilterURLParameterValues } from 'common/widgets';
import { getItems } from 'components/AvaEzm/Utilities/ODataUtility';
import FiltersPanel from 'components/Common/FiltersPanel';
import ColumnChooser from 'components/Extensions/ColumnChooser';
import {
    IpaAdministrationSearchFilters,
    IpaAdministrationSearchGridColumns,
    toolbarOptions,
} from 'components/ProviderData/IpaAdministration/atoms/IpaAdministration.Metadata';
import { createIpaMember } from 'components/ProviderData/IpaAdministration/atoms/utils';
import {
    refreshTabService,
    selectedIpaAdministrationService,
    refreshIpaFiltersService,
} from 'components/ProviderData/subjects/IpaAdministrationService';
import { countPropertiesWithValues } from 'components/ProviderData/utils/utils';
import { SectionHeader } from 'components/UI/BoxSection';
import 'components/ProviderData/IpaAdministration/organisms/IpaAdministration.css';
import 'css/styles.css';

const SEARCH_URL = 'api/MedicalGroup/SearchForIpaOrVendorsPaginated';

const INITIAL_FILTER = { ipaStatus: '1' };

const IPA_STATUS = [
    { value: '0', text: 'Show Active Only' },
    { value: '1', text: 'Show All' },
];

const IpaAdministrationSearch = () => {
    const [filters, setFilters] = useState(IpaAdministrationSearchFilters);
    const { showErrorToast } = useToast();
    const [isReadOnlyViewer, setIsReadOnlyViewer] = useState(false);
    const [dataExportImportUtilityFlag, setDataExportImportUtilityFlag] = useState(false);
    const { belongToGroupName } = useUserGroups();
    const [ipaAdminData, setIpaAdminData] = useState<DataManager | any[]>([]);
    const [filterDataIsLoaded, setFilterDataIsLoaded] = useState(false);

    const gridInstance = useRef<GridComponent | null>(null);
    const getAsyncFilters = async (_companyCode?) => {
        const _contractTypes = await getItems(`/api/MedicalGroup/GetIpaRisks`);
        const _ipaCodes = await getItems(`/api/MedicalGroup/SearchForIpa`);
        const _companyCodes = await getItems<{ companyCode: string; companyName: string }[]>(
            '/api/MedicalGroup/GetCompanyCodes',
        );

        setFilters(
            filters.map((f) => {
                if (f.id === 'companyCode') {
                    return {
                        ...f,
                        data: _companyCodes,
                    };
                } else if (f.id === 'ipaStatus') {
                    return {
                        ...f,
                        data: IPA_STATUS,
                    };
                } else if (f.id === 'ipaCode') {
                    return {
                        ...f,
                        data: _ipaCodes?.map((i: any) => ({
                            ipaCode: i.ipaCode,
                            ipaDesc: `${i.ipaCode} - ${i.ipaDesc}`,
                        })),
                        fields: { text: 'ipaDesc', value: 'ipaCode' },
                    };
                } else if (f.id === 'contractType') {
                    return {
                        ...f,
                        data: _contractTypes,
                        fields: { text: 'text', value: 'id' },
                    };
                }
                return f;
            }),
        );
        setFilterDataIsLoaded(true);
    };

    const fetchFeatureFlags = async () => {
        const flags: any = await getItems<any>('/api/MedicalGroup/GetNetworkAdminFeatureFlags');
        const _dataExportImportUtilityFlag = flags.find((f) => f.name === 'enable-export-import-utility');

        setDataExportImportUtilityFlag(_dataExportImportUtilityFlag?.isActive);
    };

    const fetchDataToUpdateDataSource = (_filters: any) => {
        if (filterDataIsLoaded) {
            if (countPropertiesWithValues(_filters) <= countPropertiesWithValues(INITIAL_FILTER)) {
                showErrorToast('Please, select at least one filter before searching the data');
                return;
            }

            const parameters = getFilterURLParameterValues({ ..._filters });

            setIpaAdminData(getAvaDataManager(`${SEARCH_URL}?${parameters.join('&')}`, WebApiAdaptor));
        }
    };

    const onToolbarClick = (args) => {
        if (args.item.id === 'currentgrid_exportexcel') {
            const btn = document.querySelector('#' + args.item.id);
            if (btn && gridInstance?.current) {
                btn.classList.add('loading', 'animatedBtn');
                gridInstance.current.toolbarModule.enableItems([args.item.id], false);
                const callback = function () {
                    btn.classList.remove('loading');
                    btn.classList.add('success');
                    setTimeout(function () {
                        btn.classList.remove('success');
                        if (gridInstance?.current?.toolbarModule) {
                            gridInstance.current.toolbarModule.enableItems([args.item.id], true);
                        }
                    }, 2000);
                };

                const excelExportProperties = {
                    includeHiddenColumn: false,
                    fileName: 'SearchResults.xlsx',
                };
                gridInstance.current.excelExport(excelExportProperties).then(() => callback());
            }
        }

        if (args.item.id === 'create-new') {
            createIpaMember(null, null);
        }
    };

    useEffect(() => {
        if (belongToGroupName('Provider Data Viewers') && !belongToGroupName('Provider Network Manager'))
            setIsReadOnlyViewer(true);
    }, [belongToGroupName]);

    useEffect(() => {
        fetchFeatureFlags();
        const controller = new AbortController();
        getAsyncFilters();
        refreshTabService.getAction().subscribe((action) => {
            if (action == 'refresh') {
                fetchDataToUpdateDataSource(filters);
            }
        });
        const refreshIpaFiltersSubscription = refreshIpaFiltersService.getAction().subscribe(() => {
            getAsyncFilters();
        });

        return () => {
            controller.abort();
            refreshIpaFiltersSubscription.unsubscribe();
        };
    }, []);

    return (
        <div
            className={`provider-data-ipa-admin-search${isReadOnlyViewer ? ' view-only' : !dataExportImportUtilityFlag ? ' hide-datatooloptions' : ''
                }`}
            id='provider-data-ipa-admin-search'
        >
            <div className='tabDiv'>
                <SectionHeader
                    cssClass='border-bottom-0'
                    title='Search Criteria'
                />
                <FiltersPanel
                    staticFilters={filters}
                    dynamicFilters={[]}
                    onClickSearch={fetchDataToUpdateDataSource}
                    initialValues={INITIAL_FILTER}
                />
                <div className='ipa-administration-search-container'>
                    <GridComponent
                        ref={gridInstance}
                        allowPaging
                        pageSettings={{ pageSize: 25 }}
                        height={400}
                        rowHeight={50}
                        dataSource={ipaAdminData}
                        columns={IpaAdministrationSearchGridColumns}
                        toolbar={toolbarOptions}
                        toolbarClick={onToolbarClick}
                        showColumnChooser={true}
                        recordDoubleClick={({ rowData }) => selectedIpaAdministrationService.select(rowData)}
                        dataBound={() => {
                            if (!gridInstance.current) return;
                            gridInstance.current.autoFitColumns();
                        }}
                        allowExcelExport={true}
                    >
                        <Inject services={[Toolbar, Page, Resize, Sort, Toolbar, ColumnChooser, ExcelExport]} />
                    </GridComponent>
                </div>
            </div>
        </div>
    );
};

export default IpaAdministrationSearch;
